"use client";

import React, { useState, useEffect, useContext } from "react";
import useAppLevelAuth from "../../hooks/useAppLevelAuth";
import { toast } from "react-hot-toast";
import axios from "axios";
import authHeader from "../../Auth/authHeader";
import { getAuthData } from "../../Auth/getAuthData";
import { ApiLink } from "../../APILink/ApiLink";
import {
  Building,
  Building2,
  ServerCog,
  Printer,
  BadgeDollarSign,
  BadgeCheck,
  ShieldCheck,
  LockKeyhole,
  Settings as SettingsIcon,
  AlertTriangle,
  Loader2,
} from "lucide-react";

import CompanyProfileTab from "./CompanyConfiguration_Components/CompanyProfileTab";
import SystemSettingsTab from "./CompanyConfiguration_Components/SystemSettingsTab";
import FiscalPrinterTab from "./CompanyConfiguration_Components/FiscalPrinterTab";
import SalesConfigTab from "./CompanyConfiguration_Components/SalesConfigTab";
import CompanyUnitsTab from "./CompanyConfiguration_Components/CompanyUnitsTab";
import UserPasswordTab from "./CompanyConfiguration_Components/UserPasswordTab";
import AuditLogsTab from "./CompanyConfiguration_Components/AuditLogsTab";
import SoftwareLicenseTab from "./CompanyConfiguration_Components/SoftwareLicenseTab";
import CrudPageHeader from "../../Components/CrudPageHeader";
import "./CompanyProfilePage.scss";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";

const mapFiscalPrinter = (value) => {
  if (String(value) === "1") return "1";
  if (String(value) === "2") return "2";
  return "0";
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toISOString().slice(0, 10);
};

export default function CompanyProfilePage() {
  const { isLoggedIn } = useAppLevelAuth();
  const authData = getAuthData();
  const userCompanyUnitID = Number(authData?.companyUnitId || 0);
  const isCompanyUnitUser = userCompanyUnitID > 0;

  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);
  const { fiscalYear } = useContext(FiscalYearContext);

  const [companyProfileData, setCompanyProfileData] = useState(null);
  const [systemSettingsData, setSystemSettingsData] = useState(null);
  const [companyUnitsData, setCompanyUnitsData] = useState([]);

  const [activeTab, setActiveTab] = useState("company");

  useEffect(() => {
    if (!isLoggedIn) return;
    checkAccessAndLoadData();
  }, [isLoggedIn]);

  const checkAccessAndLoadData = async () => {
    try {
      setLoading(true);
      const headers = authHeader();

      const [roleResponse, personalDataResponse] = await Promise.all([
        axios.get(ApiLink.API_URL + "AuthProva/getrole", { headers }),
        axios.get(ApiLink.API_URL + "PersonalData", { headers }),
      ]);

      const role = roleResponse?.data?.UserRole;
      const canManage = role === "acctree" || role === "admin";
      setIsAdmin(canManage);

      const row = Array.isArray(personalDataResponse?.data)
        ? personalDataResponse.data[0]
        : null;

      if (!row) {
        setCompanyProfileData(null);
        setSystemSettingsData(null);
        setCompanyUnitsData([]);
        return;
      }

      setCompanyProfileData({
        id: row.CompanyID,
        companyId: row.CompanyID,
        name: row.CompanyName || "",
        description: row.Comment || "",
        nui: row.IdentificationNumber || "",
        vatNumber: row.VATNumber || "",
        address: row.Address || "",
        city: row.CityName || "",
        cityId: row.CityID || "",
        country: row.StateName || "",
        stateId: row.StateID || "",
        phone: row.PhoneNumbers || "",
        email: row.Email || "",
        emailPassword: row.MailPass || "",
        atkPassword: row.EdiPass || "",
        arbkPassword: row.ARBKPass || "",
        smtpClient: row.SMTPClient || "",
        vatDeclarant: row.VATDeclarant || "",
        fiscalYear: row.FiscalYear || "",
        isActive: row.IsActive,
        logoUrl: row.CompanyLogo
          ? `${ApiLink.PHOTO_URL}${row.CompanyLogo}`
          : "",
        companyLogo: row.CompanyLogo || "",
      });

      let unitSettings = null;
      if (isCompanyUnitUser) {
        try {
          const unitResponse = await axios.get(ApiLink.API_URL + "CompanyUnits/getComUnitDetails", {
            headers,
            params: { id: userCompanyUnitID },
          });

          unitSettings = Array.isArray(unitResponse?.data) ? unitResponse.data[0] : null;
        } catch (unitError) {
          unitSettings = null;
        }
      }

      setSystemSettingsData({
        id: row.CompanyID,
        companyId: row.CompanyID,
        unitId: unitSettings?.UnitID || userCompanyUnitID || 0,
        useElectronicStamp: unitSettings?.ElectronicSeal ?? !!row.ElectronicSeal,
        negativeStockBlock: unitSettings?.NegativeStockBlock ?? !!row.NegativeStockBlock,
        periodClosingLockDate: toDateInputValue(unitSettings?.PeriodClosingLockDate ?? row.PeriodClosingLockDate),
        companyReference: {
          useElectronicStamp: !!row.ElectronicSeal,
          negativeStockBlock: !!row.NegativeStockBlock,
          periodClosingLockDate: toDateInputValue(row.PeriodClosingLockDate),
        },
        salesTypeId: (unitSettings?.SalesTypeID ?? row.SalesTypeID) ? String(unitSettings?.SalesTypeID ?? row.SalesTypeID) : "",
        mainDepo: (unitSettings?.MainDepo ?? row.MainDepo) ? String(unitSettings?.MainDepo ?? row.MainDepo) : "",
        currencyOptions: (unitSettings?.CurrencyOptions ?? row.CurrencyOptions) || "",
        activePrinterType: mapFiscalPrinter(unitSettings?.FiscalPrinter ?? row.FiscalPrinter),
        fiscalPrinterRaw: row.FiscalPrinter,
      });

      try {
        const companyUnitsResponse = await axios.get(ApiLink.API_URL + "CompanyUnits/getUnitByID", {
          headers,
          params: { id: row.CompanyID },
        });

        const units = Array.isArray(companyUnitsResponse?.data) ? companyUnitsResponse.data : [];
        setCompanyUnitsData(units);
      } catch (unitsError) {
        console.error("Failed to load company units:", unitsError);
        setCompanyUnitsData([]);
      }
    } catch (error) {
      console.error("Failed to load configuration data:", error);
      toast.error("Gabim - Nuk u ngarkuan dot rregullimet e konfigurimit.");
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) return null;

  if (loading) {
    return (
      <div className="company-profile-page company-profile-page--loading">
        <Loader2 className="company-profile-page__loading-icon" />
        <h2 className="company-profile-page__loading-title">
          Duke ngarkuar konfigurimin
        </h2>
        <p className="company-profile-page__loading-text">
          Duke pergatitur modulin e rregullimeve...
        </p>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="company-profile-page company-profile-page--denied">
        <div className="company-profile-page__alert" role="alert">
          <AlertTriangle className="company-profile-page__alert-icon" />
          <h3 className="company-profile-page__alert-title">Qasja u refuzua</h3>
          <p className="company-profile-page__alert-description">
            Moduli i konfigurimit eshte i kufizuar vetem per administratoret e
            sistemit. Roli juaj aktual nuk ka leje per te pare ose ndryshuar
            keto rregullime.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "company", label: "Kompania", icon: Building },
    ...(!isCompanyUnitUser && companyUnitsData.length > 0
      ? [{ id: "units", label: "Njesite Organizative", icon: Building2 }]
      : []),
    { id: "system", label: "Perdorimi i Sistemit", icon: ServerCog },
    { id: "fiscal", label: "Printeri Fiskal", icon: Printer },
    { id: "sales", label: "Shitje & POS", icon: BadgeDollarSign },
    { id: "license", label: "Licensa", icon: BadgeCheck },
    { id: "security", label: "Siguria", icon: LockKeyhole },
    { id: "audit", label: "Logjet e Auditimit", icon: ShieldCheck },
  ];

  return (
    <div className="company-profile-page">
      <div className="company-profile-page__shell">
        <div className="company-profile-page__header">
          <CrudPageHeader
            icon={SettingsIcon}
            title="Konfigurimi"
            description="Menaxho rregullimet e kompanise, sistemit dhe integrimeve."
            tutorialUrl="https://www.youtube.com/@Accountree"
            tutorialLabel="Shiko Tutorialin"
          />
        </div>

        <div className="company-profile-page__content-wrap">
          <div className="company-profile-page__tabs-root">
            <div className="company-profile-page__tabs-scroll">
              <div className="company-profile-page__tabs-list" role="tablist">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      className="company-profile-page__tab-trigger"
                      data-state={activeTab === tab.id ? "active" : "inactive"}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <Icon
                        className={`company-profile-page__tab-icon ${activeTab === tab.id ? "is-active" : ""}`}
                      />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="company-profile-page__tab-body">
              {activeTab === "company" && (
                <div className="company-profile-page__tab-content">
                  <CompanyProfileTab
                    initialData={companyProfileData}
                    onUpdate={setCompanyProfileData}
                  />
                </div>
              )}

              {activeTab === "units" && companyUnitsData.length > 0 && (
                <div className="company-profile-page__tab-content">
                  <CompanyUnitsTab units={companyUnitsData} />
                </div>
              )}

              {activeTab === "system" && (
                <div className="company-profile-page__tab-content">
                  <SystemSettingsTab
                    initialData={systemSettingsData}
                    onUpdate={setSystemSettingsData}
                  />
                </div>
              )}

              {activeTab === "fiscal" && (
                <div className="company-profile-page__tab-content">
                  <FiscalPrinterTab
                    initialData={systemSettingsData}
                    onUpdate={setSystemSettingsData}
                  />
                </div>
              )}

              {activeTab === "sales" && (
                <div className="company-profile-page__tab-content">
                  <SalesConfigTab
                    initialData={systemSettingsData}
                    onUpdate={setSystemSettingsData}
                  />
                </div>
              )}

              {activeTab === "security" && (
                <div className="company-profile-page__tab-content">
                  <UserPasswordTab />
                </div>
              )}

              {activeTab === "license" && (
                <div className="company-profile-page__tab-content">
                  <SoftwareLicenseTab
                    companyId={companyProfileData?.companyId}
                    accountIdentificationNumber={companyProfileData?.nui}
                  />
                </div>
              )}

              {activeTab === "audit" && (
                <div className="company-profile-page__tab-content">
                  <AuditLogsTab
                    companyId={companyProfileData?.companyId}
                    fiscalYear={fiscalYear}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
